<template>
    <fg-input label="Directions">
        <textarea ref="directions-textarea" v-model="text"
            class="directions form-control textarea-autosize"
            @input="canTabNext = false"
            @blur="handleBlur"
            :disabled="disabled" />
        <b-button v-if="oldValue" @click="undoTranslation" variant="danger" tabindex="-1">Undo Sig Translation
        </b-button>
    </fg-input>
</template>

<script lang="ts">
import { Vue, Component, Prop, Ref } from "vue-property-decorator";
import Axios from 'axios';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "DirectionsComponent",
    components: {},
})
export default class DirectionsComponent extends Vue {
    @Prop({ default: "" }) private value!: string;
    @Prop({ default: false }) private disabled!: boolean;
    @Prop({ default: null }) private programId!: number | null;
    @Prop({ default: null }) private dispensedAmount!: number | null;
    @Ref('directions-textarea') private directionsTextarea!: any;

    private oldValue: string = "";
    private canTabNext = false;
    private skipBlur = false;

    get text(): string {
        return this.value;
    }

    set text(val: string) {
        this.$emit('input', val);
    }

    created(): void {
        console.log(`%c Created ${this.$options.name}`, "color: green");
    }

    // noinspection JSUnusedGlobalSymbols
    mounted(): void {
        document.addEventListener("keydown", this.preventDefaultForHotkeys);
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy(): void {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
    }

    async preventDefaultForHotkeys(e: KeyboardEvent) {
        if (document.activeElement !== this.directionsTextarea) return;
        if (e.key === "Tab") await this.tabDown(e);
    }

    shiftTab(e: KeyboardEvent) {
        if (this.oldValue == '') return;
        if (this.oldValue != this.text) {
            e.preventDefault();
            this.skipBlur = true;
            this.undoTranslation();
        }
    }

    async tabDown(e: KeyboardEvent) {
        if (e.shiftKey) return this.shiftTab(e);
        if (!this.canTabNext) e.preventDefault();
        await this.handleBlur();
    }

    public focus() {
        (this.$refs['directions-textarea'] as any).focus();
    }

    async handleBlur() {
        if (this.skipBlur) {
            this.skipBlur = false;
            return;
        }
        if (this.text == '') return;

        try {
            const requestPayload = {
                params: {
                    instructions: this.text,
                    programId: this.programId
                }
            };
            const resp = await Axios.get<any>(`/SigTranslation/Translate`, requestPayload);
            
            // Handle the new response format with translatedInstructions and daysSupplyFactor
            const translatedInstructions = resp.data.translatedInstructions || resp.data;
            const daysSupplyFactor = resp.data.daysSupplyFactor;
            
            if (this.text != translatedInstructions) {
                this.oldValue = this.text;
                this.text = translatedInstructions;
            }
            
            // Calculate and emit days supply if we have the necessary data
            if (daysSupplyFactor && this.dispensedAmount) {
                const calculatedDaysSupply = Math.round(this.dispensedAmount * daysSupplyFactor);
                this.$emit('days-supply-calculated', calculatedDaysSupply);
            }
            
            this.canTabNext = true;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    undoTranslation() {
        this.text = this.oldValue;
        this.canTabNext = false;
    }
}
</script>

<style scoped>
.directions {
    min-height: 4.5rem;
    text-transform: uppercase;
}
</style>
